/* istanbul ignore file */
import { useMemo, useState, MouseEvent } from 'react';
import { Link as MuiLink, Typography, MenuItem, Menu, Box } from '@mui/material';
import { BreadcrumbItemProps } from '../BreadcrumbsTypes';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useShowContext } from 'framework/show';

const BreadcrumbItem = ({
  tag = 'span',
  link = '',
  active = true,
  children,
  options,
  onClickCallback,
  showOptionsWhenInactive = false,
}: BreadcrumbItemProps) => {
  const { data } = useShowContext();
  const displayedValue = useMemo(() => children, [children]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const canOpenMenu = !!options?.length && (active || showOptionsWhenInactive);

  const handleExpandButtonClick = (event: MouseEvent<HTMLElement> | null) => {
    if (event) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      {options?.length ? (
        <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
          {options
            ?.filter(option => option.label !== displayedValue)
            ?.filter(({ isVisible }) =>
              typeof isVisible === 'function' ? isVisible(data?.getData() || {}) : isVisible !== false,
            )
            .map(({ label, onChangeCallback, route, icon }) => (
              <MenuItem
                key={route}
                value={route}
                onClick={() => {
                  onChangeCallback(route);
                  handleClose();
                }}
              >
                <Box
                  display="flex"
                  alignItems="baseline"
                  justifyContent="center"
                  sx={{
                    '&>i.fa': { marginRight: 1 },
                  }}
                >
                  {icon}
                  {label}
                </Box>
              </MenuItem>
            ))}
        </Menu>
      ) : null}

      {active ? (
        <Typography
          component={tag}
          variant="subtitle1"
          color="primary"
          display="flex"
          alignItems="center"
          cursor={canOpenMenu ? 'pointer' : 'default'}
          onClick={canOpenMenu ? handleExpandButtonClick : undefined}
        >
          {displayedValue}
          {canOpenMenu && <ExpandMoreIcon />}
        </Typography>
      ) : (
        <>
          {showOptionsWhenInactive && options?.length ? (
            <MuiLink
              component="button"
              underline="hover"
              color="textSecondary"
              display="flex"
              alignItems="center"
              sx={{
                cursor: 'pointer',
                border: 0,
                background: 'transparent',
                padding: 0,
              }}
              onClick={handleExpandButtonClick}
            >
              {displayedValue}
              <ExpandMoreIcon fontSize="small" />
            </MuiLink>
          ) : (
            <MuiLink
              component={tag}
              underline="hover"
              to={link}
              color="textSecondary"
              onClick={() => onClickCallback && onClickCallback()}
            >
              {displayedValue}
            </MuiLink>
          )}
        </>
      )}
    </>
  );
};

export default BreadcrumbItem;
